// src/components/CommonApplicationForm.jsx
import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/commonapplication.css';

const CommonApplicationForm = ({ isOpen, onClose }) => {
  const formRef = useRef(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clear previous errors
    setPhoneError('');
    setNameError('');

    // 1) NAME VALIDATION - Letters only
    const formData = new FormData(formRef.current);
    const name = formData.get('name')?.trim();
    if (!name || !/^[a-zA-Z\s]+$/.test(name)) {
      setNameError('Name can only contain letters');
      const nameInput = document.getElementById('commonform-fullname');
      if (nameInput) {
        nameInput.focus();
        nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // 2) PHONE VALIDATION - Exactly 10 digits
    if (phone.length !== 10) {
      setPhoneError('Please enter a valid 10-digit mobile number.');
      const phoneInput = document.getElementById('commonform-phone');
      if (phoneInput) {
        phoneInput.focus();
        phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // 3) Native validation for other fields
    const form = formRef.current;
    if (!form.checkValidity()) {
      const firstInvalid = form.querySelector(':invalid');
      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    // 4) Prepare EmailJS data with tracking
    const templateParams = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      city: formData.get('college'), // Using 'college' field as city/college selection
      course: formData.get('course'),
      qualification: 'Not specified',
      marks: 'Not specified',
      message: 'Applied via Common Application Popup',
      // ✅ CORRECT TRACKING FIELDS
      college_name: formData.get('college') || 'General - Common Application',
      form_source: 'common_application_popup',
      page_url: window.location.href,
      submission_time: new Date().toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };
    
    // 5) Send via EmailJS
    emailjs
      .send(
        'service_u0g0bqj',
        'template_naso7ph',
        templateParams,
        '0JNndpSn5KZm_5yLJ'
      )
      .then(() => {
        setLoading(false);
        setShowSuccess(true);
        formRef.current.reset();
        setPhone('');
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 3000);
      })
      .catch((error) => {
        setLoading(false);
        console.error('EmailJS error', error);
        alert('Failed to submit. Please try again.');
      });
  };

  return (
    <div className="common-apply-overlay" onClick={onClose}>
      <div className="common-apply-modal slide-in" onClick={(e) => e.stopPropagation()}>
        <div className="common-apply-header">
          <h3>Common Application Form</h3>
          <button
            type="button"
            className="common-apply-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="common-apply-content">
          {!showSuccess ? (
            <form
              ref={formRef}
              className="common-apply-form"
              noValidate
              onSubmit={handleSubmit}
            >
              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="commonform-fullname">Full Name *</label>
                <input 
                  id="commonform-fullname" 
                  name="name" 
                  type="text" 
                  placeholder="Enter your full name (letters only)"
                  required 
                  style={{
                    borderColor: nameError ? '#ef4444' : '#e2e8f0',
                    backgroundColor: nameError ? '#fee2e2' : '#f8fafc'
                  }}
                />
                {nameError && <p className="error-text">{nameError}</p>}
              </div>

              {/* Mobile Number */}
              <div className="form-group">
                <label htmlFor="commonform-phone">Mobile Number *</label>
                <input
                  id="commonform-phone"
                  name="phone"
                  type="tel"
                  value={phone}
                  maxLength="10"
                  placeholder="10-digit mobile number"
                  required
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhone(digits);
                    setPhoneError('');
                  }}
                  style={{
                    borderColor: phoneError ? '#ef4444' : '#e2e8f0',
                    backgroundColor: phoneError ? '#fee2e2' : '#f8fafc'
                  }}
                />
                {phoneError && <p className="error-text">{phoneError}</p>}
              </div>

              {/* Select College */}
              <div className="form-group">
                <label htmlFor="commonform-college">Select College *</label>
                <select id="commonform-college" name="college" required>
                  <option value="">Choose a college</option>
                  <option value="UPES College">UPES College</option>
                  <option value="Graphic Era University">Graphic Era University</option>
                  <option value="UIT College">UIT College</option>
                  <option value="HIT College">HIT College</option>
                  <option value="Dev Bhoomi Uttarakhand University">Dev Bhoomi Uttarakhand University</option>
                  <option value="GRD College">GRD College</option>
                  <option value="DD College">DD College</option>
                  <option value="Sai College">Sai College</option>
                  <option value="Institute Of Technology & Management">Institute Of Technology & Management</option>
                  <option value="Doon Group Of Colleges">Doon Group Of Colleges</option>
                  <option value="Maya Group of Colleges">Maya Group of Colleges</option>
                  <option value="Not Sure">Not Sure / Need Guidance</option>
                </select>
              </div>

              {/* Email */}
              <div className="form-group">
                <label htmlFor="commonform-email">Email *</label>
                <input 
                  id="commonform-email" 
                  name="email" 
                  type="email" 
                  placeholder="your.email@example.com"
                  required 
                />
              </div>

              {/* Course Name */}
              <div className="form-group">
                <label htmlFor="commonform-course">Course Name *</label>
                <select id="commonform-course" name="course" required>
                  <option value="">Select a course</option>
                  <option value="B.Tech CSE">B.Tech CSE</option>
                  <option value="B.Tech Mechanical">B.Tech Mechanical</option>
                  <option value="B.Tech Civil">B.Tech Civil</option>
                  <option value="B.Tech ECE">B.Tech ECE</option>
                  <option value="BBA">BBA</option>
                  <option value="BCA">BCA</option>
                  <option value="MBA">MBA</option>
                  <option value="MCA">MCA</option>
                  <option value="BSc">BSc</option>
                  <option value="BA">BA</option>
                  <option value="BCom">BCom</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button 
                type="submit" 
                className="common-submit-button"
                disabled={loading}
                style={{
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          ) : (
            <div className="common-success-message">
              <div className="success-icon">✓</div>
              <h3 className="success-title">Application Submitted!</h3>
              <p className="success-text">
                Thank you for your interest. Our team will contact you shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommonApplicationForm;
